import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, tap } from 'rxjs/operators';
import { LoggerService } from '../../core/services/logger/logger.service';
import { Logger } from '../../core/services/logger/logger.model';
import { LayerMapFeatures, GoogleMapFeatures } from './models/layer-map-features.model';
import { DistanceService } from '../../core/services/distance/distance.service';
import { FeatureRenderingUtilityService } from 'src/app/modules/cop/drawing/features/feature-rendering-utility.service';
import { Dataset } from '../../core/store/models/dataset.model';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { MapEventsService } from '../services/map-events.service';
import { MapDrawingStateService } from './map-drawing-state.service';

@Injectable({
  providedIn: 'root'
})
export class MapPolygonVisibilityService implements OnDestroy {
  logger: Logger;
  destroy$: Subject<void> = new Subject<void>();

  constructor(
    private loggerService: LoggerService,
    private mapEventsService: MapEventsService,
    private distanceService: DistanceService,
    private mapDrawingStateService: MapDrawingStateService
  ) {
    this.logger = this.loggerService.getLogger('MapPolygonVisibilityService');

    this.mapEventsService.mapBoundsChanged$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(visibleLayerUIds => this.processMapMoveForPolygonVisibility(visibleLayerUIds));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  processMapMoveForPolygonVisibility(layerUIdsToScan: string[]) {
    const mapBounds = this.mapDrawingStateService.googleMap?.getBounds();

    if (!mapBounds) {
      return;
    }

    const mapFeatures = this.mapDrawingStateService.mapFeatures;
    if (!mapFeatures) {
      return;
    }

    for (let layerKey of layerUIdsToScan) {
      if (!this.mapDrawingStateService.mapState.layersSet || !this.mapDrawingStateService.mapState.datasetsSet) {
        continue;
      }

      const layer = this.mapDrawingStateService.mapState.layersSet[layerKey];
      if (!layer || !layer?.datasetUId) {
        continue;
      }

      const dataset = this.mapDrawingStateService.mapState.datasetsSet[layer.datasetUId];
      if (!dataset) {
        continue;
      }

      const layerFeatures: LayerMapFeatures = mapFeatures[layerKey];
      if (!layerFeatures) {
        continue;
      }

      const geoField = FeatureRenderingUtilityService.getGeometryFieldSystemName(dataset, layer);

      this.processLayerFeaturesForPolygonVisibility(dataset, layer, layerFeatures, mapBounds);
    }
  }

  processLayerFeaturesForPolygonVisibility(
    dataset: Dataset,
    layer: Layer,
    layerMapFeatures: LayerMapFeatures,
    mapBounds: google.maps.LatLngBounds
  ) {
    const documentKeys = Object.keys(layerMapFeatures);

    for (let documentKey of documentKeys) {
      const googleMapFeatures = layerMapFeatures[documentKey];

      if (!!document && !!googleMapFeatures && googleMapFeatures.polygons?.length > 0) {
        setTimeout(() => {
          this.processGoogleMapFeaturesForPolygonVisibility(mapBounds, googleMapFeatures);
        }, 0);
      }
    }
  }

  processGoogleMapFeaturesForPolygonVisibility(mapBounds: google.maps.LatLngBounds, googleMapFeatures: GoogleMapFeatures) {
    // get the boundary of the polygon and compare it to the bounds of the map. If the poly
    // contains the map then we should hide it so that it does not cover the screen.
    // if not covered then make sure they are visible
    for (let poly of googleMapFeatures.polygons) {
      const polyBounds = this.getGooglePolygonBounds(poly);
      if (!polyBounds) {
        continue;
      }

      const docContainsTheMap = this.distanceService.doesBoundsContainBounds(mapBounds, polyBounds);

      poly.setVisible(!docContainsTheMap);

      const documentUId = poly['__data']?.documentUId ?? null;

      if (documentUId) {
        const overlay = googleMapFeatures?.polygonOverlays?.find(o => o['__data']?.documentUId === documentUId);

        if (Boolean(overlay)) {
          overlay.setVisible(!docContainsTheMap);
        }
      }
    }
  }

  getGooglePolygonBounds(poly: google.maps.Polygon): google.maps.LatLngBounds {
    var bounds = new google.maps.LatLngBounds();
    if (!bounds) {
      return null;
    }

    const path = poly.getPath();
    if (!path) {
      return null;
    }

    poly.getPath().forEach(function (element, index) {
      bounds.extend(element);
    });

    return bounds;
  }
}
